import { useRealtimeStore } from "@/lib/store/Realtime";
import { ChatComponent } from "../../components/chat/chat.component";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import MessageIcon from "@mui/icons-material/Message";
import { Tables } from "@/lib/supabase/supabaseTypesImport";
import {
	ChatSidebar,
	ChatSidebarTab,
} from "../../components/chat/chat-sidebar.component";
import { useParams, useNavigate } from "react-router-dom";
import { useCentralStore } from "../../store/Central";
import { useMemo } from "react";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { AcceptJobRequestResult } from "../../store/Jobs/job.store";

interface ChatsPageProps {
	organizationId: string;
	userId: string;
	onShareFile: (fileId: string) => Promise<void>;
	onUnshareFile: (fileId: string) => Promise<void>;
	onAcceptJobRequest: (
		channelId: string | null
	) => Promise<AcceptJobRequestResult>;
	onDenyJobRequest: (channelId: string | null) => Promise<boolean>;
	hideTabs?: Omit<ChatSidebarTab, "all">[];
	clientOrganizationIds?: string[];
}

export const ChatsPage: React.FC<ChatsPageProps> = (props) => {
	const navigate = useNavigate();
	const handleSelectChannel = (channel: Tables<"message_channels">) => {
		if (channel.id) {
			navigate(`/chats/${channel.id}`);
		}
	};
	return (
		<div className="h-full flex">
			<HeaderPortal>
				<PageTitle
					title={[{ name: "Chats" }]}
					icon={
						<MessageIcon
							sx={{
								width: "16px",
								height: "16px",
								color: "rgb(55, 65, 81)",
							}}
						/>
					}
				/>
			</HeaderPortal>
			<ChatsPageInner {...props} onSelectChannel={handleSelectChannel} />
		</div>
	);
};

export const ChatsPageInner: React.FC<
	ChatsPageProps & {
		customerOrganizationId?: string;
		onSelectChannel: (channel: Tables<"message_channels">) => void;
	}
> = ({
	organizationId,
	userId,
	onShareFile,
	onUnshareFile,
	onAcceptJobRequest,
	onDenyJobRequest,
	hideTabs = [],
	clientOrganizationIds,
	customerOrganizationId,
	onSelectChannel,
}) => {
	const params = useParams<{ channelId?: string }>();

	const { latestChannels } = useRealtimeStore((state) => ({
		latestChannels: state.latestChannels,
	}));
	const { clientsLookup } = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
	}));
	// Create reverse lookup map from organization ID to client name
	const orgIdToClientNameMap = useMemo(() => {
		const map: Record<string, string> = {};
		Object.values(clientsLookup || {}).forEach((client) => {
			const clientName = getClientDisplayName(client);
			if (client.connected_organization_id && clientName) {
				map[client.connected_organization_id] = clientName;
			}
		});
		return map;
	}, [clientsLookup]);

	const channels = useMemo(() => {
		return customerOrganizationId
			? latestChannels.filter(
					(channel) =>
						channel.customer_org_id === customerOrganizationId
				)
			: latestChannels;
	}, [latestChannels, customerOrganizationId]);

	return (
		<>
			<ChatSidebar
				channels={channels}
				selectedChannelId={params.channelId || undefined}
				onSelectChannel={onSelectChannel}
				organizationId={organizationId}
				userId={userId}
				className="w-80 flex-shrink-0"
				hideTabs={hideTabs}
				orgIdToClientNameMap={orgIdToClientNameMap}
			/>

			<div className="flex-1 bg-white">
				{params.channelId ? (
					<ChatComponent
						organizationId={organizationId}
						channelId={params.channelId}
						onShareFile={onShareFile}
						onUnshareFile={onUnshareFile}
						onAcceptJobRequest={onAcceptJobRequest}
						onDenyJobRequest={onDenyJobRequest}
						clientOrganizationIds={clientOrganizationIds}
						allowGoTo={true}
						checkForClientConnectionToCustomer={true}
					/>
				) : (
					<div className="flex items-center justify-center h-full text-gray-500">
						<p>Wählen Sie einen Chat aus, um zu beginnen</p>
					</div>
				)}
			</div>
		</>
	);
};
